import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { searchAll } from "../api";

const TYPE_BADGE = {
  movie: { label: "movie",  bg: "rgba(9,9,11,0.82)", color: "#9898a6" },
  show:  { label: "series", bg: "rgba(9,9,11,0.82)", color: "#9b8de8" },
};

function ResultCard({ item }) {
  const navigate   = useNavigate();
  const isShow     = item.media_type === "show";
  const path       = isShow ? `/show/${item.id}` : `/movie/${item.id}`;
  const title      = item.title || item.name || "";
  const initials   = title.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase() || "?";
  const firstGenre = item.genres?.[0]?.trim() || "";
  const score      = isShow ? item.guffgaff_score : item.score;
  const badge      = TYPE_BADGE[item.media_type] || TYPE_BADGE.movie;

  return (
    <div
      onClick={() => navigate(path)}
      style={{
        background: "#0f0f12", border: "0.5px solid #1e1e22",
        borderRadius: 14, overflow: "hidden", cursor: "pointer",
        transition: "border-color 0.2s, transform 0.2s, box-shadow 0.2s",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = "rgba(255,92,53,0.45)";
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "0 8px 28px rgba(255,92,53,0.1)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = "#1e1e22";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{
        height: 130, background: "#161618", position: "relative",
        overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {item.poster_url ? (
          <img src={item.poster_url} alt={title}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={e => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 30, fontWeight: 800, color: "#2a2a32" }}>
            {initials}
          </span>
        )}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, transparent 50%, rgba(9,9,11,0.55) 100%)", pointerEvents: "none" }} />
        {/* Type badge */}
        <div style={{
          position: "absolute", top: 7, left: 7,
          background: badge.bg, backdropFilter: "blur(6px)",
          borderRadius: 5, padding: "2px 7px",
          fontSize: 9, fontWeight: 700, color: badge.color,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          letterSpacing: "0.04em", textTransform: "uppercase",
          border: `0.5px solid ${isShow ? "rgba(155,141,232,0.25)" : "rgba(255,255,255,0.08)"}`,
        }}>
          {badge.label}
        </div>
      </div>
      <div style={{ padding: "11px 13px 13px" }}>
        <div style={{
          fontFamily: "'Bricolage Grotesque', sans-serif",
          fontWeight: 700, fontSize: 12, color: "#e8e8ee",
          lineHeight: 1.3, marginBottom: 3,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>{title}</div>
        <div style={{ fontSize: 10, color: "#45454e", marginBottom: 8, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {item.year}{firstGenre ? ` · ${firstGenre}` : ""}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
          <span style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 19, fontWeight: 800,
            background: "linear-gradient(135deg, #FF5C35, #ff8a68)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
          }}>{score ?? "—"}</span>
          <span style={{ fontSize: 10, color: "#45454e" }}>/10</span>
        </div>
      </div>
    </div>
  );
}

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") || "";

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!query) { setLoading(false); return; }
    setLoading(true);
    setResults([]);
    searchAll(query)
      .then(d => setResults(d.results || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [query]);

  const movieCount = results.filter(r => r.media_type !== "show").length;
  const showCount  = results.filter(r => r.media_type === "show").length;

  return (
    <div style={{ minHeight: "100vh", background: "#0b0b0c" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "28px clamp(16px, 4vw, 20px)" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "#111113", border: "0.5px solid #252528",
              color: "#9898a6", borderRadius: 20, padding: "6px 14px",
              fontSize: 12, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif",
              display: "flex", alignItems: "center", gap: 4,
              transition: "border-color 0.15s, color 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#3a3a44"; e.currentTarget.style.color = "#c0c0cc"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#252528"; e.currentTarget.style.color = "#9898a6"; }}
          >← back</button>
          <h1 style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 20, fontWeight: 800, color: "#f0f0f0", letterSpacing: "-0.4px",
          }}>
            results for <span style={{ color: "#FF5C35" }}>"{query}"</span>
          </h1>
          {!loading && results.length > 0 && (
            <span style={{ fontSize: 12, color: "#3a3a44", fontFamily: "'Plus Jakarta Sans', sans-serif", marginLeft: "auto" }}>
              {movieCount > 0 && `${movieCount} movie${movieCount !== 1 ? "s" : ""}`}
              {movieCount > 0 && showCount > 0 && " · "}
              {showCount > 0 && `${showCount} series`}
            </span>
          )}
        </div>

        {/* Results grid */}
        <div className="movie-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(155px, 1fr))", gap: 14 }}>
          {loading ? (
            <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "48px 0", color: "#3a3a44", fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              searching…
            </div>
          ) : results.length === 0 ? (
            <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "64px 0" }}>
              <div style={{ fontSize: 40, marginBottom: 14 }}>🔍</div>
              <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 16, fontWeight: 700, color: "#3a3a44", marginBottom: 8 }}>
                no results for "{query}"
              </div>
              <div style={{ fontSize: 13, color: "#2a2a32", marginBottom: 24, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                try a different title or browse all films
              </div>
              <button onClick={() => navigate("/")} style={{
                background: "#FF5C35", color: "#fff", border: "none",
                padding: "10px 24px", borderRadius: 20, fontSize: 12,
                fontWeight: 700, cursor: "pointer", fontFamily: "'Bricolage Grotesque', sans-serif",
                boxShadow: "0 4px 16px rgba(255,92,53,0.3)",
              }}>browse all</button>
            </div>
          ) : (
            results.map(item => <ResultCard key={`${item.media_type}-${item.id}`} item={item} />)
          )}
        </div>

      </div>
    </div>
  );
}
